import React from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Input, Row } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchDropdown from './SearchDropdown';

const searchOptionsId = 'search-options';
const focusableSelector = 'input:not([disabled]), button:not([disabled])';

/*
The search field opens a panel of search options and the Search button. The field is a combobox
that says whether the panel is open; ArrowDown moves into the panel and Escape closes it. The panel
is plain markup rather than a reactstrap dropdown, whose key handling swallowed Enter and Space on
the panel's buttons and whose toggles were unlabelled spans.
*/
class SearchBox extends React.Component {
  _isMounted = false;
  formRef = React.createRef();
  inputRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      showSearch: false,
      value: ''
    };

    this.clearForm = this.clearForm.bind(this);
    this.closeOnOutsideEvent = this.closeOnOutsideEvent.bind(this);
    this.closeSearchOptions = this.closeSearchOptions.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleOptionsKeyDown = this.handleOptionsKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.toggleSearchOptions = this.toggleSearchOptions.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    document.addEventListener('mousedown', this.closeOnOutsideEvent);
    document.addEventListener('focusin', this.closeOnOutsideEvent);
  }

  componentWillUnmount() {
    this._isMounted = false;
    document.removeEventListener('mousedown', this.closeOnOutsideEvent);
    document.removeEventListener('focusin', this.closeOnOutsideEvent);
  }

  // a click or focus anywhere outside the search form closes the panel, as the dropdown did
  closeOnOutsideEvent(event) {
    const form = this.formRef.current;
    if (this.state.showSearch && form && !form.contains(event.target)) {
      this.closeSearchOptions();
    }
  }

  clearForm() {
    this._isMounted && this.setState({ value: '' });

    const { clearSearch } = this.props;
    clearSearch('search');
  }

  closeSearchOptions() {
    this._isMounted && this.setState({ showSearch: false });
  }

  handleChange(event) {
    this._isMounted && this.setState({ value: event.target.value, showSearch: true });
  }

  handleInputKeyDown(event) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this._isMounted &&
        this.setState({ showSearch: true }, () => {
          const first = document.getElementById(searchOptionsId).querySelector(focusableSelector);
          first && first.focus();
        });
    } else if (event.key === 'Escape' && this.state.showSearch) {
      event.preventDefault();
      this.closeSearchOptions();
    }
  }

  handleOptionsKeyDown(event) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.closeSearchOptions();
      this.inputRef.current && this.inputRef.current.focus();
    }
  }

  handleSubmit(searchOptions) {
    const { value } = this.state;
    const { clearSearch, searchWithKey } = this.props;

    if (value && value.trim() !== '') {
      this._isMounted && this.setState({ showSearch: false });
      searchWithKey({ query: value, options: searchOptions });
    } else clearSearch('search');
  }

  toggleSearchOptions() {
    this._isMounted && this.setState(state => ({ showSearch: !state.showSearch }));
  }

  render() {
    const { pageTitle } = this.props;
    const { showSearch } = this.state;
    return (
      <Row>
        <div className="col-sm col-md-6 pl-md-3">
          <Form className="search-box mt-2" onSubmit={this.handleSubmit} innerRef={this.formRef}>
            <div>
              <Input
                id="searchField"
                innerRef={this.inputRef}
                placeholder="Search..."
                aria-label="Search"
                role="combobox"
                aria-haspopup="dialog"
                aria-expanded={showSearch}
                aria-controls={searchOptionsId}
                value={this.state.value}
                className="rounded-pill pb-4 pt-4 pl-5 pr-5 mb-0"
                onChange={this.handleChange}
                onClick={this.toggleSearchOptions}
                onKeyDown={this.handleInputKeyDown}
                required
                autoComplete="off"
              />
              <FontAwesomeIcon icon="search" className="position-absolute text-400 fs-1 search-box-icon ml-2 ml-md-1" />
            </div>
            {this.state.value && (
              <button
                type="button"
                aria-label="Clear search"
                className="p-0 border-0 bg-transparent cursor-pointer dropdown-menu-right mr-2 pr-2 absolute-vertical-center"
                onClick={this.clearForm}
              >
                <FontAwesomeIcon icon="times" className="text-400 fs-4" />
              </button>
            )}

            <div className="position-relative">
              <SearchDropdown
                id={searchOptionsId}
                isOpen={showSearch}
                onClearAll={this.clearForm}
                onKeyDown={this.handleOptionsKeyDown}
                onSubmitForm={this.handleSubmit}
                pageTitle={pageTitle}
                toggle={this.toggleSearchOptions}
              />
            </div>
          </Form>
        </div>
        <div className="col-sm col-md-5 mt-auto mb-auto pt-2 pb-2 d-none d-md-block">
          <Button color="primary" onClick={this.clearForm}>
            View all Content
          </Button>
        </div>
      </Row>
    );
  }
}

SearchBox.propTypes = {
  clearSearch: PropTypes.func,
  pageTitle: PropTypes.string,
  searchWithKey: PropTypes.func
};

SearchBox.defaultProps = {
  clearSearch: () => {},
  pageTitle: 'search',
  searchWithKey: () => {}
};

export default SearchBox;
